import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Card,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Tag,
  Space,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tabs,
  Empty,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  TagsOutlined,
  ArrowLeftOutlined,
  ImportOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { TextArea } = Input;

interface Question {
  id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'fill_blank';
  options: string[];
  correct_answer: string;
  points: number;
  knowledge_points: string[];
  category: string;
  created_at: string;
}

interface Category {
  category: string;
  count: number;
}

interface KnowledgePoint {
  name: string;
  count: number;
}

const QuestionBank: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [knowledgePoints, setKnowledgePoints] = useState<KnowledgePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedKP, setSelectedKP] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/teacher/login');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [questionsRes, categoriesRes, kpRes] = await Promise.all([
        axios.get('/api/question-bank', {
          params: {
            category: selectedCategory,
            knowledge_point: selectedKP
          }
        }),
        axios.get('/api/question-bank/categories'),
        axios.get('/api/question-bank/knowledge-points')
      ]);
      
      const parsedQuestions = questionsRes.data.map((q: any) => ({
        ...q,
        options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options || [],
        knowledge_points: typeof q.knowledge_points === 'string' ? JSON.parse(q.knowledge_points) : q.knowledge_points || []
      }));
      
      setQuestions(parsedQuestions);
      setCategories(categoriesRes.data);
      setKnowledgePoints(kpRes.data);
    } catch (error) {
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchQuestions();
    }
  }, [selectedCategory, selectedKP]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/question-bank', {
        params: {
          category: selectedCategory,
          knowledge_point: selectedKP
        }
      });
      
      const parsedQuestions = res.data.map((q: any) => ({
        ...q,
        options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options || [],
        knowledge_points: typeof q.knowledge_points === 'string' ? JSON.parse(q.knowledge_points) : q.knowledge_points || []
      }));
      
      setQuestions(parsedQuestions);
    } catch (error) {
      message.error('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingQuestion(null);
    form.resetFields();
    form.setFieldsValue({
      question_type: 'multiple_choice',
      points: 1,
      options: ['A. ', 'B. ', 'C. ', 'D. ']
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: Question) => {
    setEditingQuestion(record);
    form.setFieldsValue({
      ...record,
      knowledge_points: record.knowledge_points?.join(', ') || ''
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/api/question-bank/${id}`);
      message.success('Question deleted');
      fetchData();
    } catch (error) {
      message.error('Failed to delete question');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const kpArray = values.knowledge_points
        ? values.knowledge_points.split(',').map((s: string) => s.trim()).filter((s: string) => s)
        : [];
      
      const data = {
        ...values,
        knowledge_points: kpArray,
        options: values.options || []
      };

      if (editingQuestion) {
        await axios.put(`/api/question-bank/${editingQuestion.id}`, data);
        message.success('Question updated');
      } else {
        await axios.post('/api/question-bank', data);
        message.success('Question added');
      }
      
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('Failed to save question');
    }
  };

  const getTypeTag = (type: string) => {
    const types: Record<string, { color: string; label: string }> = {
      multiple_choice: { color: 'blue', label: 'Multiple Choice' },
      true_false: { color: 'green', label: 'True/False' },
      fill_blank: { color: 'orange', label: 'Fill Blank' }
    };
    const t = types[type] || { color: 'default', label: type };
    return <Tag color={t.color}>{t.label}</Tag>;
  };

  const columns = [
    {
      title: 'Question',
      dataIndex: 'question_text',
      key: 'question_text',
      ellipsis: true,
      width: '30%'
    },
    {
      title: 'Type',
      dataIndex: 'question_type',
      key: 'question_type',
      width: 120,
      render: (type: string) => getTypeTag(type)
    },
    {
      title: 'Answer',
      dataIndex: 'correct_answer',
      key: 'correct_answer',
      width: 100
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 80
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cat: string) => cat ? <Tag>{cat}</Tag> : '-'
    },
    {
      title: 'Knowledge Points',
      dataIndex: 'knowledge_points',
      key: 'knowledge_points',
      width: 200,
      render: (kps: string[]) => (
        <Space wrap size={[0, 4]}>
          {kps?.slice(0, 2).map((kp, i) => (
            <Tag key={i} color="purple">{kp}</Tag>
          ))}
          {kps?.length > 2 && <Tag>+{kps.length - 2}</Tag>}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Question) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm title="Delete this question?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' }}>
      <Card style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/teacher')}>
              Back
            </Button>
            <h2 style={{ margin: 0 }}>
              <BookOutlined style={{ marginRight: 8 }} />
              Question Bank
            </h2>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Question
          </Button>
        </div>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Questions" value={questions.length} prefix={<BookOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Categories" value={categories.length} prefix={<TagsOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Knowledge Points" value={knowledgePoints.length} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Avg Points" value={questions.length > 0 ? (questions.reduce((a, b) => a + b.points, 0) / questions.length).toFixed(1) : 0} />
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="all" items={[
          {
            key: 'all',
            label: 'All Questions',
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Select
                    placeholder="Filter by category"
                    allowClear
                    style={{ width: 200 }}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                  >
                    {categories.map(c => (
                      <Option key={c.category} value={c.category}>{c.category} ({c.count})</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Filter by knowledge point"
                    allowClear
                    style={{ width: 200 }}
                    value={selectedKP}
                    onChange={setSelectedKP}
                  >
                    {knowledgePoints.map(kp => (
                      <Option key={kp.name} value={kp.name}>{kp.name} ({kp.count})</Option>
                    ))}
                  </Select>
                  {(selectedCategory || selectedKP) && (
                    <Button onClick={() => { setSelectedCategory(null); setSelectedKP(null); }}>
                      Clear Filters
                    </Button>
                  )}
                </Space>
                <Table
                  columns={columns}
                  dataSource={questions}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: <Empty description="No questions in your bank yet. Add your first question!" /> }}
                />
              </>
            )
          },
          {
            key: 'categories',
            label: 'By Category',
            children: (
              <Row gutter={[16, 16]}>
                {categories.length === 0 ? (
                  <Col span={24}>
                    <Empty description="No categories yet" />
                  </Col>
                ) : (
                  categories.map(cat => (
                    <Col span={8} key={cat.category}>
                      <Card 
                        hoverable 
                        onClick={() => { setSelectedCategory(cat.category); }}
                        style={{ textAlign: 'center' }}
                      >
                        <Statistic title={cat.category} value={cat.count} suffix="questions" />
                      </Card>
                    </Col>
                  ))
                )}
              </Row>
            )
          },
          {
            key: 'knowledge',
            label: 'By Knowledge Point',
            children: (
              <Row gutter={[16, 16]}>
                {knowledgePoints.length === 0 ? (
                  <Col span={24}>
                    <Empty description="No knowledge points yet" />
                  </Col>
                ) : (
                  knowledgePoints.map(kp => (
                    <Col span={6} key={kp.name}>
                      <Card 
                        hoverable 
                        onClick={() => { setSelectedKP(kp.name); }}
                        style={{ textAlign: 'center' }}
                      >
                        <Tag color="purple" style={{ fontSize: 14, padding: '4px 12px' }}>{kp.name}</Tag>
                        <div style={{ marginTop: 8, color: '#666' }}>{kp.count} questions</div>
                      </Card>
                    </Col>
                  ))
                )}
              </Row>
            )
          }
        ]} />

        <Modal
          title={editingQuestion ? 'Edit Question' : 'Add Question'}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={700}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="question_text" label="Question" rules={[{ required: true }]}>
              <TextArea rows={3} placeholder="Enter question text" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="question_type" label="Type" rules={[{ required: true }]}>
                  <Select>
                    <Option value="multiple_choice">Multiple Choice</Option>
                    <Option value="true_false">True/False</Option>
                    <Option value="fill_blank">Fill Blank</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="points" label="Points" rules={[{ required: true }]}>
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.question_type !== curr.question_type}>
              {({ getFieldValue }) => {
                const type = getFieldValue('question_type');
                if (type === 'multiple_choice') {
                  return (
                    <Form.List name="options" initialValue={['A. ', 'B. ', 'C. ', 'D. ']}>
                      {(fields, { add, remove }) => (
                        <Form.Item label="Options">
                          {fields.map((field, index) => (
                            <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }}>
                              <Form.Item {...field} noStyle>
                                <Input placeholder={`Option ${index + 1}`} style={{ width: 400 }} />
                              </Form.Item>
                              {fields.length > 2 && (
                                <Button type="text" danger onClick={() => remove(field.name)}>Remove</Button>
                              )}
                            </Space>
                          ))}
                          {fields.length < 6 && (
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                              Add Option
                            </Button>
                          )}
                        </Form.Item>
                      )}
                    </Form.List>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item name="correct_answer" label="Correct Answer" rules={[{ required: true }]}>
              <Input placeholder="Enter correct answer (e.g., A, B, True, or the fill-in answer)" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="category" label="Category">
                  <Input placeholder="e.g., Math, Physics, History" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="knowledge_points" label="Knowledge Points (comma separated)">
                  <Input placeholder="e.g., Algebra, Equations" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingQuestion ? 'Update' : 'Add'} Question
                </Button>
                <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default QuestionBank;
